import { useLocation, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { EventListItem } from '../types'

// useBreadcrumb - derives the topbar breadcrumb trail from the current URL.
//
// First crumb is always "Heritage Ops" linking to /. Subsequent crumbs are
// route-derived. For dynamic segments (/events/:id) we read the event name
// from the existing query cache rather than firing a fresh request - if the
// cache is empty (e.g. landed straight on the URL via a bookmark), we fall
// back to "Event".
//
// `to` is null for the final crumb (no self-link) and for the dynamic
// /events/:id leaf per spec 02 §"Open questions".

export interface Crumb {
  label: string
  to: string | null
}

const STATIC_LABELS: Record<string, string> = {
  '/': 'Heritage Ops',
  '/events': 'Events',
  '/calendar': 'Calendar',
  '/my-actions': 'My Actions & Tasks',
  '/reports': 'Reports',
  '/admin': 'Admin'
}

export function useBreadcrumb(): { trail: Crumb[] } {
  const { pathname } = useLocation()
  const params = useParams<{ id?: string }>()
  const qc = useQueryClient()

  const trail: Crumb[] = [{ label: 'Heritage Ops', to: pathname === '/' ? null : '/' }]

  if (pathname === '/') {
    return { trail }
  }

  if (pathname.startsWith('/events/') && params.id) {
    trail.push({ label: 'Events', to: '/events' })
    const id = Number(params.id)
    let name = 'Event'
    if (Number.isFinite(id) && id > 0) {
      const cached = qc.getQueryData<EventListItem>(['events', id])
      if (cached?.name) name = cached.name
    }
    trail.push({ label: name, to: null })
    return { trail }
  }

  const label = STATIC_LABELS[pathname]
  if (label) {
    trail.push({ label, to: null })
  } else {
    // Unknown route - derive a best-effort label from the last segment.
    const seg = pathname.split('/').filter(Boolean).pop() ?? ''
    trail.push({ label: prettify(seg), to: null })
  }
  return { trail }
}

function prettify(segment: string): string {
  if (!segment) return ''
  return segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
